import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Plus, Trash2, Edit2, Eye, EyeOff, ExternalLink, X, Linkedin, Send, Loader2, Copy, RefreshCcw } from "lucide-react";

const CATS = [
  { id: "regulated_financial_licence", label: "Regulated Financial Licence" },
  { id: "business_stake", label: "Business Stake" },
  { id: "technology_ip", label: "Technology / IP" },
  { id: "fund_structure", label: "Fund Structure" },
  { id: "igaming_platform", label: "iGaming / Platform" },
  { id: "commercial_real_estate", label: "Commercial Real Estate" },
];
const REGIONS = [
  { id: "eu_regulated", label: "EU Regulated" },
  { id: "midshore_offshore", label: "Mid-Shore & Offshore" },
  { id: "north_america", label: "North America" },
  { id: "global", label: "Global" },
];
const STATUSES = ["new", "verified", "available", "under_offer", "reserved", "recently_sold"];
const ENQ_STAGES = ["new", "nda_pending", "pack_sent", "in_discussion", "introduced", "closed", "lost"];
const SUB_STAGES = ["submitted", "under_review", "accepted", "declined"];

const emptyListing = {
  title: "", category: "regulated_financial_licence", jurisdiction: "",
  region_group: "eu_regulated", regulator: "", regulatory_status: "",
  tags: [], status_badge: "available", featured: false, published: true,
  teaser_summary: "", highlights: [], year_established: null,
  banking_included: false, passporting: false,
  full_description: "", asking_price: "",
};

export default function NexusAdminPanel() {
  const [tab, setTab] = useState("listings");
  const [listings, setListings] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [buyside, setBuyside] = useState([]);
  const [ndaAcceptances, setNdaAcceptances] = useState([]);
  const [buyers, setBuyers] = useState([]);
  const [deals, setDeals] = useState([]);
  const [editing, setEditing] = useState(null);
  const [teaserFor, setTeaserFor] = useState(null);
  const [dealFor, setDealFor] = useState(null);

  const loadAll = () => {
    api.get("/nexus/admin/listings").then((r) => setListings(r.data.items)).catch(() => {});
    api.get("/nexus/admin/enquiries").then((r) => setEnquiries(r.data.items)).catch(() => {});
    api.get("/nexus/admin/submissions").then((r) => setSubmissions(r.data.items)).catch(() => {});
    api.get("/nexus/admin/buy-side-requests").then((r) => setBuyside(r.data.items)).catch(() => {});
    api.get("/nexus/admin/nda-requests").then((r) => setNdaAcceptances(r.data.items)).catch(() => {});
    api.get("/nexus/admin/buyers").then((r) => setBuyers(r.data.items)).catch(() => {});
    api.get("/nexus/admin/deals").then((r) => setDeals(r.data.items)).catch(() => {});
  };
  useEffect(loadAll, []);

  const save = async (payload, id) => {
    try {
      if (id) await api.put(`/nexus/admin/listings/${id}`, payload);
      else await api.post("/nexus/admin/listings", payload);
      toast.success("Saved");
      setEditing(null);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this listing?")) return;
    await api.delete(`/nexus/admin/listings/${id}`);
    toast.success("Deleted");
    loadAll();
  };
  const updateEnq = async (id, stage) => {
    await api.put(`/nexus/admin/enquiries/${id}`, { stage });
    loadAll();
  };
  const updateSub = async (id, stage) => {
    await api.put(`/nexus/admin/submissions/${id}`, { stage });
    loadAll();
  };

  return (
    <div data-testid="nexus-admin">
      <div className="flex flex-wrap items-center gap-2 mb-6">
        {[
          { id: "listings", label: `Listings (${listings.length})` },
          { id: "deals", label: `Deals (${deals.length})` },
          { id: "nda", label: `NDA Acceptances (${ndaAcceptances.length})` },
          { id: "submissions", label: `Submissions (${submissions.length})` },
          { id: "buyside", label: `Buy-side (${buyside.length})` },
          { id: "buyers", label: `Buyers (${buyers.length})` },
          { id: "enquiries", label: `Enquiries (${enquiries.length})` },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`nexus-admin-tab-${t.id}`}
            className={`tag ${tab === t.id ? "tag-active" : ""}`}
          >
            {t.label}
          </button>
        ))}
        {tab === "listings" && (
          <button
            onClick={() => setEditing({ ...emptyListing })}
            data-testid="nexus-admin-new-listing"
            className="btn-primary !py-2 !px-4 ml-auto inline-flex items-center gap-2"
          >
            <Plus size={14} /> New listing
          </button>
        )}
      </div>

      {tab === "listings" && (
        <div className="border border-[var(--line)]" data-testid="nexus-admin-listings">
          <div className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-[var(--line)] font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--mute)]">
            <div className="col-span-4">Title</div>
            <div className="col-span-2">Jurisdiction</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-1">Views</div>
            <div className="col-span-1">Enq.</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>
          {listings.map((l) => (
            <div key={l.id} className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-[var(--line)] items-center">
              <div className="col-span-4">
                <div className="text-[var(--bone)] font-serif">{l.title}</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--mute-2)]">/{l.slug}</div>
              </div>
              <div className="col-span-2 text-sm">{l.jurisdiction} · {l.regulator || "—"}</div>
              <div className="col-span-2 text-xs uppercase">
                <span className="font-mono">{l.status_badge}</span>{l.featured && " · ★"}{!l.published && " · draft"}
              </div>
              <div className="col-span-1 font-mono text-xs">{l.view_count || 0}</div>
              <div className="col-span-1 font-mono text-xs">{l.enquiry_count || 0}</div>
              <div className="col-span-2 flex justify-end gap-2">
                <a href={`/nexus/listings/${l.slug}`} target="_blank" rel="noopener noreferrer" title="View" className="p-2 text-[var(--mute)] hover:text-[var(--bone)]">
                  <ExternalLink size={14} />
                </a>
                <button onClick={() => setTeaserFor(l)} title="LinkedIn teaser" className="p-2 text-[var(--mute)] hover:text-[#C9896E]" data-testid={`nexus-teaser-btn-${l.id}`}>
                  <Linkedin size={14} />
                </button>
                <button onClick={() => setEditing(l)} title="Edit" className="p-2 text-[var(--mute)] hover:text-[var(--bone)]">
                  <Edit2 size={14} />
                </button>
                <button onClick={() => del(l.id)} title="Delete" className="p-2 text-[var(--mute)] hover:text-red-400">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "enquiries" && <PipelineList rows={enquiries} stages={ENQ_STAGES} onStage={updateEnq} idKey="id" mainKey="listing_title" subKey="buyer_email" msgKey="message" testid="nexus-enq" />}
      {tab === "submissions" && <PipelineList rows={submissions} stages={SUB_STAGES} onStage={updateSub} idKey="id" mainKey="asset_type" subKey="contact_email" msgKey="description" testid="nexus-sub" />}

      {tab === "nda" && (
        <div className="border border-[var(--line)]" data-testid="nexus-admin-nda">
          {ndaAcceptances.length === 0 ? (
            <div className="p-8 text-center text-[var(--mute)]">No NDA acceptances recorded yet.</div>
          ) : ndaAcceptances.map((n) => (
            <div key={n.id} className="p-5 border-b border-[var(--line)]">
              <div className="flex items-start justify-between gap-4 mb-2">
                <div>
                  <div className="text-[var(--bone)] font-serif text-lg">{n.full_name} <span className="text-[var(--mute)] text-sm">· {n.position || "—"} · {n.company_name || "—"}</span></div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--mute-2)]">{n.email} · {n.phone || "—"}</div>
                </div>
                <div className="text-right text-xs text-[var(--mute)] font-mono">
                  <div>{n.reference}</div>
                  <div>NDA v{n.nda_version || "—"}</div>
                  <div>{(n.accepted_at || "").slice(0,19).replace("T"," ")}</div>
                </div>
              </div>
              <div className="text-sm text-[var(--bone-2)] mb-1">→ {n.listing_title}</div>
              {n.registration_number && <div className="text-xs text-[var(--mute)]">Reg. no. {n.registration_number}</div>}
              <div className="text-[10px] text-[var(--mute-2)] font-mono mt-1">IP {n.ip} · {(n.user_agent || "").slice(0,80)}</div>
            </div>
          ))}
        </div>
      )}

      {tab === "buyers" && (
        <div className="border border-[var(--line)]" data-testid="nexus-admin-buyers">
          {buyers.length === 0 ? (
            <div className="p-8 text-center text-[var(--mute)]">No buyer qualifications yet.</div>
          ) : buyers.map((b) => (
            <div key={b.id} className="p-5 border-b border-[var(--line)]">
              <div className="flex items-start justify-between gap-4 mb-2">
                <div>
                  <div className="text-[var(--bone)] font-serif text-lg">{b.full_name} <span className="text-[var(--mute)] text-sm">· {b.company || "—"}</span></div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--mute-2)]">{b.email} · {b.buyer_type} {b.qualified ? "· qualified" : ""}</div>
                </div>
                <div className="text-right text-xs text-[var(--mute)] font-mono">
                  {b.budget_band || "—"} · {b.timeframe || "—"}
                </div>
              </div>
              <div className="text-sm text-[var(--bone-2)] mb-1">
                Interests: {b.asset_interests?.join(", ") || "—"}
              </div>
              <div className="text-xs text-[var(--mute)]">
                Jurisdictions: {b.jurisdictions?.join(", ") || "—"}
              </div>
              {b.notes && <p className="text-sm text-[var(--mute)] mt-2">{b.notes}</p>}
            </div>
          ))}
        </div>
      )}

      {tab === "buyside" && (
        <div className="border border-[var(--line)]" data-testid="nexus-admin-buyside">
          {buyside.length === 0 ? (
            <div className="p-8 text-center text-[var(--mute)]">No buy-side requests yet.</div>
          ) : buyside.map((b) => (
            <div key={b.id} className="p-5 border-b border-[var(--line)]">
              <div className="flex items-start justify-between gap-4 mb-2">
                <div>
                  <div className="text-[var(--bone)] font-serif text-lg">{b.contact_name} <span className="text-[var(--mute)] text-sm">· {b.company || "—"}</span></div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--mute-2)]">{b.contact_email}</div>
                </div>
                <div className="text-right text-xs text-[var(--mute)] font-mono">
                  {b.budget_band || "—"} · {b.timeframe || "—"}
                </div>
              </div>
              <div className="text-sm text-[var(--bone-2)] mb-2">{b.category || "any category"} · {b.jurisdictions?.join(", ") || "any jurisdictions"} · {b.regulator || "any regulator"}</div>
              <p className="text-sm text-[var(--mute)]">{b.notes}</p>
            </div>
          ))}
        </div>
      )}

      {tab === "deals" && (
        <div className="border border-[var(--line)]" data-testid="nexus-admin-deals">
          {deals.length === 0 ? (
            <div className="p-8 text-center text-[var(--mute)]">No deal threads yet. Deals open automatically when a buyer accepts an NDA on any listing.</div>
          ) : deals.map((d) => (
            <button
              key={d.id}
              onClick={() => setDealFor(d)}
              className="w-full text-left p-5 border-b border-[var(--line)] hover:bg-white/[0.02] transition-colors"
              data-testid={`nexus-admin-deal-${d.id}`}
            >
              <div className="flex items-start justify-between gap-4 mb-2">
                <div className="min-w-0">
                  <div className="text-[var(--bone)] font-serif text-lg truncate">{d.listing_title}</div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--mute-2)] mt-1">
                    {d.buyer_name} · {d.buyer_email} · {d.buyer_company || "—"}
                  </div>
                </div>
                <div className="text-right shrink-0 text-xs text-[var(--mute)] font-mono">
                  <div>{d.reference}</div>
                  {d.unread_admin > 0 && (
                    <span className="inline-block mt-1 text-[10px] font-mono uppercase tracking-[0.18em] bg-[#C9896E] text-black px-2 py-0.5">
                      {d.unread_admin} new
                    </span>
                  )}
                </div>
              </div>
              <div className="text-[10px] text-[var(--mute-2)] font-mono">
                Last activity {new Date(d.last_message_at).toLocaleString("en-GB")}
              </div>
            </button>
          ))}
        </div>
      )}

      {editing && (
        <ListingEditor
          initial={editing}
          onClose={() => setEditing(null)}
          onSave={(payload) => save(payload, editing.id)}
        />
      )}
      {teaserFor && (
        <TeaserModal
          listing={teaserFor}
          onClose={() => { setTeaserFor(null); loadAll(); }}
        />
      )}
      {dealFor && (
        <DealModal
          initial={dealFor}
          onClose={() => { setDealFor(null); loadAll(); }}
        />
      )}
    </div>
  );
}

function PipelineList({ rows, stages, onStage, idKey, mainKey, subKey, msgKey, testid }) {
  return (
    <div className="border border-[var(--line)]" data-testid={`${testid}-list`}>
      {rows.length === 0 ? (
        <div className="p-8 text-center text-[var(--mute)]">Nothing here yet.</div>
      ) : rows.map((r) => (
        <div key={r[idKey]} className="p-5 border-b border-[var(--line)]">
          <div className="flex items-start justify-between gap-4 mb-2">
            <div>
              <div className="text-[var(--bone)] font-serif text-lg">{r[mainKey]}</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--mute-2)]">{r[subKey]}</div>
            </div>
            <select value={r.stage} onChange={(e) => onStage(r[idKey], e.target.value)} className="field !w-auto text-xs">
              {stages.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <p className="text-sm text-[var(--mute)] line-clamp-3">{r[msgKey]}</p>
          <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--mute-2)]">
            {new Date(r.created_at).toLocaleString("en-GB")}
          </div>
        </div>
      ))}
    </div>
  );
}

function ListingEditor({ initial, onClose, onSave }) {
  const [f, setF] = useState({
    ...initial,
    tags_text: (initial.tags || []).join(", "),
    highlights_text: (initial.highlights || []).join("\n"),
  });
  const on = (e) => {
    const { name, value, type, checked } = e.target;
    setF((s) => ({ ...s, [name]: type === "checkbox" ? checked : value }));
  };
  const submit = (e) => {
    e.preventDefault();
    onSave({
      title: f.title,
      category: f.category,
      jurisdiction: f.jurisdiction,
      region_group: f.region_group,
      regulator: f.regulator,
      regulatory_status: f.regulatory_status,
      tags: f.tags_text.split(",").map((t) => t.trim()).filter(Boolean),
      status_badge: f.status_badge,
      featured: !!f.featured,
      published: !!f.published,
      teaser_summary: f.teaser_summary,
      highlights: f.highlights_text.split("\n").map((h) => h.trim()).filter(Boolean),
      year_established: f.year_established ? parseInt(f.year_established, 10) : null,
      banking_included: !!f.banking_included,
      passporting: !!f.passporting,
      full_description: f.full_description || "",
      asking_price: f.asking_price || "",
    });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center overflow-y-auto py-10" onClick={onClose} data-testid="nexus-listing-editor">
      <div className="bg-[var(--ink)] border border-[var(--line)] max-w-3xl w-full mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-[var(--line)] px-6 py-4">
          <h3 className="font-serif text-xl">{initial.id ? "Edit listing" : "New listing"}</h3>
          <button onClick={onClose} className="text-[var(--mute)] hover:text-[var(--bone)]"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="p-6 grid gap-4 md:grid-cols-2">
          <Field label="Title *" name="title" value={f.title} onChange={on} required />
          <Field label="Jurisdiction *" name="jurisdiction" value={f.jurisdiction} onChange={on} required />
          <div>
            <label className="field-label">Category</label>
            <select name="category" value={f.category} onChange={on} className="field">
              {CATS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <label className="field-label">Region group</label>
            <select name="region_group" value={f.region_group} onChange={on} className="field">
              {REGIONS.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
            </select>
          </div>
          <Field label="Regulator" name="regulator" value={f.regulator} onChange={on} />
          <Field label="Regulatory status" name="regulatory_status" value={f.regulatory_status} onChange={on} />
          <div>
            <label className="field-label">Status badge</label>
            <select name="status_badge" value={f.status_badge} onChange={on} className="field">
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <Field label="Year established" name="year_established" type="number" value={f.year_established || ""} onChange={on} />
          <div className="md:col-span-2">
            <label className="field-label">Tags (comma-separated)</label>
            <input name="tags_text" value={f.tags_text} onChange={on} className="field" placeholder="MiCA, Crypto, EU" />
          </div>
          <div className="md:col-span-2">
            <label className="field-label">Teaser summary *</label>
            <textarea name="teaser_summary" required rows={3} maxLength={600} value={f.teaser_summary} onChange={on} className="field" />
          </div>
          <div className="md:col-span-2">
            <label className="field-label">Highlights (one per line)</label>
            <textarea name="highlights_text" rows={4} value={f.highlights_text} onChange={on} className="field" />
          </div>
          <div className="md:col-span-2">
            <label className="field-label">Full description (gated)</label>
            <textarea name="full_description" rows={5} maxLength={6000} value={f.full_description} onChange={on} className="field" />
          </div>
          <Field label="Asking price / terms (gated)" name="asking_price" value={f.asking_price} onChange={on} />
          <div className="md:col-span-2 flex flex-wrap gap-5 pt-2">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" name="published" checked={!!f.published} onChange={on} /> Published</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" name="featured" checked={!!f.featured} onChange={on} /> Featured</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" name="banking_included" checked={!!f.banking_included} onChange={on} /> Banking included</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" name="passporting" checked={!!f.passporting} onChange={on} /> Passporting</label>
          </div>
          <div className="md:col-span-2 flex justify-end gap-3 pt-4 border-t border-[var(--line)]">
            <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
            <button type="submit" className="btn-primary" data-testid="nexus-editor-save">Save listing</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, ...props }) {
  return (
    <div>
      <label className="field-label">{label}</label>
      <input className="field" {...props} />
    </div>
  );
}

function TeaserModal({ listing, onClose }) {
  const [teaser, setTeaser] = useState(listing.linkedin_teaser || null);
  const [post, setPost] = useState(listing.linkedin_teaser?.post || "");
  const [hashtags, setHashtags] = useState((listing.linkedin_teaser?.hashtags || []).join(" "));
  const [busy, setBusy] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const generate = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/nexus/admin/listings/${listing.id}/generate-teaser`);
      setTeaser(r.data);
      setPost(r.data.post || "");
      setHashtags((r.data.hashtags || []).join(" "));
      setEditMode(false);
      toast.success("Teaser generated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed");
    } finally { setBusy(false); }
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      const tags = hashtags.split(/\s+/).map((t) => t.trim()).filter(Boolean);
      const r = await api.put(`/nexus/admin/listings/${listing.id}/teaser`, { post, hashtags: tags });
      setTeaser(r.data);
      setEditMode(false);
      toast.success("Teaser saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const copyAll = async () => {
    const tags = (teaser?.hashtags || []).join(" ");
    const combined = `${teaser?.post || post}\n\n${tags}`.trim();
    try {
      await navigator.clipboard.writeText(combined);
      toast.success("Copied to clipboard");
    } catch { toast.error("Could not access clipboard"); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center overflow-y-auto py-10" onClick={onClose} data-testid="nexus-teaser-modal">
      <div className="bg-[var(--ink)] border border-[var(--line)] max-w-2xl w-full mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-[var(--line)] px-6 py-4">
          <div>
            <h3 className="font-serif text-xl inline-flex items-center gap-2"><Linkedin size={16} className="text-[#C9896E]" /> LinkedIn teaser</h3>
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--mute)] mt-1">{listing.title}</div>
          </div>
          <button onClick={onClose} className="text-[var(--mute)] hover:text-[var(--bone)]"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-5">
          {!teaser && !busy && (
            <div className="text-center py-10">
              <p className="text-[var(--bone-2)] mb-6 text-sm leading-relaxed max-w-md mx-auto">
                Generate a redacted LinkedIn-safe post + hashtag block for this listing using Claude Haiku. Nothing that could identify the seller is included.
              </p>
              <button onClick={generate} className="btn-primary inline-flex items-center gap-2" data-testid="nexus-teaser-generate">
                <Send size={14} /> Generate teaser
              </button>
            </div>
          )}

          {busy && (
            <div className="text-center py-10">
              <Loader2 className="animate-spin mx-auto text-[#C9896E] mb-3" size={22} />
              <div className="text-sm text-[var(--mute)]">Claude Haiku is drafting…</div>
            </div>
          )}

          {teaser && !busy && (
            <>
              <div>
                <label className="field-label">Post text</label>
                {editMode ? (
                  <textarea rows={10} maxLength={3000} value={post} onChange={(e) => setPost(e.target.value)} className="field" data-testid="nexus-teaser-post-edit" />
                ) : (
                  <div className="border border-[var(--line)] p-4 bg-black/30 whitespace-pre-wrap text-sm text-[var(--bone)] leading-relaxed" data-testid="nexus-teaser-post-view">
                    {teaser.post}
                  </div>
                )}
              </div>
              <div>
                <label className="field-label">Hashtags</label>
                {editMode ? (
                  <input value={hashtags} onChange={(e) => setHashtags(e.target.value)} placeholder="#Tag1 #Tag2 …" className="field" data-testid="nexus-teaser-hashtags-edit" />
                ) : (
                  <div className="flex flex-wrap gap-2" data-testid="nexus-teaser-hashtags-view">
                    {(teaser.hashtags || []).map((h, i) => (
                      <span key={i} className="text-xs font-mono text-[#C9896E] border border-[#C9896E]/30 px-2 py-1">{h}</span>
                    ))}
                  </div>
                )}
              </div>
              <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--mute-2)]">
                Generated {(teaser.generated_at || "").slice(0, 19).replace("T", " ")}
                {teaser.edited_at && ` · Edited ${teaser.edited_at.slice(0, 19).replace("T", " ")}`}
              </div>
              <div className="flex flex-wrap justify-end gap-2 pt-3 border-t border-[var(--line)]">
                {editMode ? (
                  <>
                    <button onClick={() => { setEditMode(false); setPost(teaser.post); setHashtags((teaser.hashtags || []).join(" ")); }} className="btn-ghost" data-testid="nexus-teaser-cancel-edit">Cancel</button>
                    <button onClick={saveEdit} className="btn-primary" data-testid="nexus-teaser-save-edit">Save</button>
                  </>
                ) : (
                  <>
                    <button onClick={generate} className="btn-ghost inline-flex items-center gap-2" data-testid="nexus-teaser-regenerate">
                      <RefreshCcw size={12} /> Regenerate
                    </button>
                    <button onClick={() => { setPost(teaser.post); setHashtags((teaser.hashtags || []).join(" ")); setEditMode(true); }} className="btn-ghost inline-flex items-center gap-2" data-testid="nexus-teaser-edit">
                      <Edit2 size={12} /> Edit
                    </button>
                    <button onClick={copyAll} className="btn-primary inline-flex items-center gap-2" data-testid="nexus-teaser-copy">
                      <Copy size={12} /> Copy
                    </button>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function DealModal({ initial, onClose }) {
  const [deal, setDeal] = useState(null);
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/nexus/admin/deals/${initial.id}`)
      .then((r) => { setDeal(r.data.deal); setMessages(r.data.messages || []); })
      .catch(() => toast.error("Could not load deal"))
      .finally(() => setLoading(false));
  }, [initial.id]);

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setBusy(true);
    try {
      const r = await api.post(`/nexus/admin/deals/${initial.id}/messages`, { body: body.trim() });
      setMessages((prev) => [...prev, r.data]);
      setBody("");
      toast.success("Reply sent");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center overflow-y-auto py-10" onClick={onClose} data-testid="nexus-deal-modal">
      <div className="bg-[var(--ink)] border border-[var(--line)] max-w-2xl w-full mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-[var(--line)] px-6 py-4">
          <div className="min-w-0">
            <h3 className="font-serif text-xl truncate">{initial.listing_title}</h3>
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--mute)] mt-1">
              {initial.buyer_name} · {initial.buyer_email} · {initial.reference}
            </div>
          </div>
          <button onClick={onClose} className="text-[var(--mute)] hover:text-[var(--bone)] shrink-0"><X size={18} /></button>
        </div>
        <div className="max-h-[440px] overflow-y-auto px-6 py-5 space-y-4" data-testid="nexus-deal-messages">
          {loading ? (
            <div className="text-center py-10"><Loader2 className="animate-spin mx-auto text-[#C9896E]" size={20} /></div>
          ) : messages.length === 0 ? (
            <div className="text-[var(--mute)] text-sm">No messages yet.</div>
          ) : messages.map((m) => {
            const isCorpora = m.from_role === "corpora";
            const isSystem = m.from_role === "system";
            if (isSystem) {
              return <div key={m.id} className="text-center text-[11px] font-mono uppercase tracking-[0.18em] text-[var(--mute-2)] py-1">{m.body}</div>;
            }
            return (
              <div key={m.id} className={`flex ${isCorpora ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] ${isCorpora ? "bg-[#C9896E]/10 border-[#C9896E]/25" : "bg-white/[0.05] border-white/10"} border px-4 py-3`}>
                  <div className="font-mono text-[10px] uppercase tracking-[0.18em] mb-1 opacity-60">
                    {isCorpora ? "Corpora Consultants" : "Buyer"} · {new Date(m.created_at).toLocaleString("en-GB")}
                  </div>
                  <div className="text-sm text-[var(--bone)] whitespace-pre-wrap leading-relaxed">{m.body}</div>
                </div>
              </div>
            );
          })}
        </div>
        <form onSubmit={send} className="border-t border-[var(--line)] px-6 py-4 flex gap-3 items-end">
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={2}
            maxLength={5000}
            placeholder="Reply as Corpora Consultants…"
            className="field flex-1"
            data-testid="nexus-deal-reply-input"
          />
          <button type="submit" className="btn-primary shrink-0 inline-flex items-center gap-2" disabled={busy || !body.trim()} data-testid="nexus-deal-reply-send">
            {busy ? <Loader2 className="animate-spin" size={14} /> : <><Send size={14} /> Send</>}
          </button>
        </form>
      </div>
    </div>
  );
}
